import { useContext, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Button, Flex, Input } from 'antd';
import { useNavigate } from 'react-router-dom';

import { useGetVideosCollections } from '@/api/monitor_collections';
import { useDebounce } from '@/hooks';
import { CollectionContext } from '@/providers/CollectionsContextProvider';
import { IPagination, TCollection } from '@/ts';

import AddCollectionModal from '../AddCollectionModal';
import EditCollectionModal from '../EditCollectionModal';
import VideoCollectionsTable from './VideoCollectionsTable';
import { collectionsColumns as videoCollectionsColumns } from './data';

export default function VideosCollections() {
	const navigate = useNavigate();
	const { setCollection, collection, setData, modalData, setModalData } =
		useContext(CollectionContext);
	const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
	const [isEditCollectionModalOpen, setIsEditCollectionModalOpen] =
		useState(false);

	const [search, setSearch] = useState('');
	const [sort, setSort] = useState<'created_at' | 'status'>('created_at');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

	const [pagination, setPagination] = useState<IPagination>({
		page: 1,
		pageCount: 1,
		total: 0,
		limit: 10,
	});

	const debouncedSearch = useDebounce(search);

	const {
		data: videosCollectionsData,
		isFetching: isVideosCollectionFetching,
		isLoading: isVideosCollectionLoading,
		isError: isVideosCollectionError,
	} = useGetVideosCollections({
		pagination,
		sortBy: sort,
		sortOrder,
		enabled: collection === 'videos',
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
	});

	const onTableChange = (__: any, _: any, sorter: any) => {
		setSort(sorter.field ?? 'created_at');
		setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
	};

	const onEditCollectionClick = (data: any) => {
		setModalData(null);
		setIsEditCollectionModalOpen(true);
		setModalData(data);
	};

	const onViewClick = (collection: TCollection, data: any) => {
		setData(data);
		setCollection(collection);
		navigate(
			`/app/dashboard/video-insights-co-pilot/collections/videos/${data?.id}`,
		);
	};

	if (collection !== 'videos') {
		return null;
	}

	const items = videosCollectionsData?.data?.data?.items || [];
	const total = videosCollectionsData?.data?.data?.pagination?.total || 0;

	return (
		<Flex vertical gap={16} align="flex-start" style={{ width: '100%' }}>
			<Flex vertical gap={16} align="flex-start" style={{ width: '100%' }}>
				<Flex
					align="center"
					justify="space-between"
					gap={16}
					style={{ width: '100%' }}
				>
					<Input
						placeholder="Search..."
						suffix={<SearchOutlined />}
						// style={{ width: "200px", maxWidth: "200px" }}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
					<Button type="primary" onClick={() => setIsCollectionModalOpen(true)}>
						New video collection
					</Button>
				</Flex>
			</Flex>
			<VideoCollectionsTable
				data={items}
				columns={videoCollectionsColumns}
				onTableChange={onTableChange}
				isLoading={isVideosCollectionLoading}
				isFetching={isVideosCollectionFetching}
				pagination={{ ...pagination, total }}
				setPagination={setPagination}
				onViewClick={onViewClick}
				onEditClick={onEditCollectionClick}
			/>
			<AddCollectionModal
				isOpen={isCollectionModalOpen}
				setOpen={setIsCollectionModalOpen}
				type="videos"
			/>
			<EditCollectionModal
				isOpen={isEditCollectionModalOpen}
				setOpen={setIsEditCollectionModalOpen}
				data={{
					...modalData,
					type: 'videos',
				}}
				setData={setModalData}
			/>
		</Flex>
	);
}
